import React, { useState, useEffect } from "react";
import {
  Trophy,
  XCircle,
  AlertTriangle,
  Sparkles,
  Clock,
  FileSpreadsheet,
  CheckCircle2,
  AlertOctagon,
  ExternalLink,
  ChevronRight,
  PlusCircle,
  ArrowRight,
  HelpCircle,
  Loader2,
  TrendingUp,
} from "lucide-react";
import { Opportunity, UserProfile, DebriefFactor, Answer } from "../../types";
import { PostMortemModal } from "../pipeline/PostMortemModal";
import { HarvestLibraryModal } from "./HarvestLibraryModal";
import { cloneOpportunityForRebid, getDaysUntilRebidNotice } from "../../services/rebidService";
import { flagAnswerForReview } from "../../services/harvestService";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../lib/firebase";

export interface DebriefTabProps {
  opportunity: Opportunity;
  user: UserProfile | null;
  onSwitchTab?: (tab: string) => void;
  onOpenHarvest?: () => void;
}

const FACTOR_LABELS: Record<DebriefFactor, { label: string; positive: boolean }> = {
  wbe_certification: { label: "WBE Certification Advantage", positive: true },
  technical_solution: { label: "Technical Solution & Methodology", positive: true },
  pricing: { label: "Pricing / Cost Proposal", positive: false },
  past_performance: { label: "Past Performance & References", positive: true },
  incumbent_advantage: { label: "Incumbent Advantage (Competitor)", positive: false },
  compliance_gap: { label: "Compliance & Required Form Gaps", positive: false },
  turnaround: { label: "Turnaround & Delivery Capacity", positive: true },
  timeline: { label: "Timeline Feasibility", positive: false },
  relationship: { label: "Client Relationship & Trust", positive: true },
  other: { label: "Other Deciding Factors", positive: false },
};

export const DebriefTab: React.FC<DebriefTabProps> = ({ opportunity, user, onOpenHarvest }) => {
  const [isPostMortemModalOpen, setIsPostMortemModalOpen] = useState(false);
  const [isHarvestModalOpen, setIsHarvestModalOpen] = useState(false);

  // Credited Answer Details
  const [creditedAnswers, setCreditedAnswers] = useState<Answer[]>([]);
  const [loadingAnswers, setLoadingAnswers] = useState(false);

  // Rebid Cloning State
  const [isCloningRebid, setIsCloningRebid] = useState(false);
  const [rebidSuccessId, setRebidSuccessId] = useState<string | null>(null);
  const [cloneError, setCloneError] = useState<string | null>(null);

  // Flag Weak Answer State
  const [flaggingAnswerId, setFlaggingAnswerId] = useState<string | null>(null);
  const [flagReason, setFlagReason] = useState("");
  const [isSubmittingFlag, setIsSubmittingFlag] = useState(false);

  const postMortem = opportunity.postMortem;
  const outcome = opportunity.outcome;
  const isDecided = !!outcome;

  // Load credited answer records
  useEffect(() => {
    let isMounted = true;
    const creditedIds = postMortem?.creditedAnswerIds || [];

    const loadCredited = async () => {
      if (creditedIds.length === 0) {
        if (isMounted) setCreditedAnswers([]);
        return;
      }

      setLoadingAnswers(true);
      try {
        const results: Answer[] = [];
        for (const id of creditedIds.slice(0, 10)) {
          const snap = await getDoc(doc(db, "answers", id));
          if (snap.exists()) {
            results.push({ id: snap.id, ...(snap.data() as Omit<Answer, "id">) });
          }
        }
        if (isMounted) setCreditedAnswers(results);
      } catch (err) {
        console.error("Error loading credited answers:", err);
      } finally {
        if (isMounted) setLoadingAnswers(false);
      }
    };

    void loadCredited();
    return () => {
      isMounted = false;
    };
  }, [postMortem?.creditedAnswerIds]);

  const handleInitializeRebid = async () => {
    if (!user) return;
    setIsCloningRebid(true);
    setCloneError(null);

    try {
      // Calculate a default due date 2 years out or based on maxExpirationDate
      const defaultDueDate =
        opportunity.rebidTracking?.maxExpirationDate ||
        new Date(Date.now() + 730 * 24 * 60 * 60 * 1000).toISOString().split("T")[0] ||
        "";

      const newId = await cloneOpportunityForRebid({
        originalOpportunity: opportunity,
        user,
        newDueDate: defaultDueDate,
      });

      setRebidSuccessId(newId);
    } catch (err) {
      console.error("Failed to initialize rebid opportunity:", err);
      setCloneError(err instanceof Error ? err.message : "Failed to initialize rebid.");
    } finally {
      setIsCloningRebid(false);
    }
  };

  const handleFlagAnswer = async (answerId: string) => {
    if (!flagReason.trim()) return;
    setIsSubmittingFlag(true);
    try {
      await flagAnswerForReview(
        answerId,
        flagReason.trim(),
        opportunity.id,
        user ? { displayName: user.displayName, email: user.email } : undefined,
      );
      setFlaggingAnswerId(null);
      setFlagReason("");
    } catch (err) {
      console.error("Failed to flag answer:", err);
    } finally {
      setIsSubmittingFlag(false);
    }
  };

  const rebidDays = opportunity.rebidTracking?.rebidCaptureNoticeDate
    ? getDaysUntilRebidNotice(opportunity.rebidTracking.rebidCaptureNoticeDate)
    : null;

  return (
    <div className="space-y-6">
      {/* Tab Header Banner */}
      <div className="flex flex-col gap-4 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-xs sm:flex-row sm:items-center sm:justify-between dark:border-slate-800 dark:bg-slate-900">
        <div>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-teal-100 px-2.5 py-0.5 text-xs font-bold text-teal-800 dark:bg-teal-950 dark:text-teal-300">
              Stage 7 of 7
            </span>
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
              Learn & Knowledge Feedback Loop
            </span>
          </div>
          <h2 className="mt-1 text-xl font-bold tracking-tight text-slate-900 dark:text-white">
            Post-Mortem Debrief & Living Library
          </h2>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Record win/loss drivers, client evaluator scorecards, attribute win credit, and schedule
            contract rebids.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setIsPostMortemModalOpen(true)}
            className="btn-animate inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-xs font-semibold text-slate-800 shadow-2xs hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <Trophy className="h-4 w-4 text-amber-500" />
            <span>{isDecided ? "Edit Debrief Record" : "Record Bid Outcome"}</span>
          </button>

          <button
            type="button"
            onClick={() => {
              if (onOpenHarvest) onOpenHarvest();
              else setIsHarvestModalOpen(true);
            }}
            className="btn-animate inline-flex items-center gap-1.5 rounded-xl bg-[#2ba5b5] px-3.5 py-2 text-xs font-semibold text-white shadow-2xs hover:bg-[#238a97]"
          >
            <Sparkles className="h-4 w-4" />
            <span>Harvest to Library</span>
          </button>
        </div>
      </div>

      {/* STATE A: DECISION PENDING */}
      {!isDecided && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-amber-200/80 bg-amber-50/60 p-6 text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-200">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-600 dark:bg-amber-900/50 dark:text-amber-300">
                  <Clock className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-amber-900 dark:text-amber-100">
                    Client Decision Pending (Stage 6 Submitted)
                  </h3>
                  <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
                    This proposal has been officially submitted and is awaiting client evaluation
                    and contract award notice.
                  </p>
                  {opportunity.officialSubmission && (
                    <div className="mt-2.5 flex flex-wrap items-center gap-3 text-xs">
                      <span>
                        Dispatched via:{" "}
                        <strong>{opportunity.officialSubmission.method.toUpperCase()}</strong>
                      </span>
                      <span>·</span>
                      <span>
                        Date:{" "}
                        <strong>{opportunity.officialSubmission.submittedAt.slice(0, 10)}</strong>
                      </span>
                      {opportunity.officialSubmission.confirmationNumber && (
                        <>
                          <span>·</span>
                          <span>
                            Tracking #:{" "}
                            <strong>{opportunity.officialSubmission.confirmationNumber}</strong>
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsPostMortemModalOpen(true)}
                className="btn-animate shrink-0 inline-flex items-center gap-2 rounded-xl bg-amber-600 px-4 py-2.5 text-xs font-bold text-white shadow-sm hover:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-600"
              >
                <Trophy className="h-4 w-4" />
                <span>Record Award / Decision</span>
              </button>
            </div>
          </div>

          {/* Pending Guidance Cards */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-2xs dark:border-slate-800 dark:bg-slate-900">
              <div className="flex items-center gap-2.5 font-bold text-slate-900 dark:text-white text-sm">
                <FileSpreadsheet className="h-4 w-4 text-[#2ba5b5]" />
                <h4>Client Debrief & FOIA Best Practices</h4>
              </div>
              <ul className="mt-3 space-y-2 text-xs text-slate-600 dark:text-slate-400">
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-3.5 w-3.5 mt-0.5 text-[#2ba5b5] shrink-0" />
                  <span>
                    Public agencies typically allow formal debrief requests within{" "}
                    <strong>3–5 business days</strong> of notice.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-3.5 w-3.5 mt-0.5 text-[#2ba5b5] shrink-0" />
                  <span>
                    Request evaluator scorecards, pricing sheets, and the winning vendor&apos;s
                    redacted proposal via FOIA / Illinois FOIA.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-3.5 w-3.5 mt-0.5 text-[#2ba5b5] shrink-0" />
                  <span>
                    Record the scores here to refine our pricing thresholds and narrative win themes
                    for future bids.
                  </span>
                </li>
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-2xs dark:border-slate-800 dark:bg-slate-900">
              <div className="flex items-center gap-2.5 font-bold text-slate-900 dark:text-white text-sm">
                <Sparkles className="h-4 w-4 text-[#2ba5b5]" />
                <h4>Living Library Knowledge Pre-Harvest</h4>
              </div>
              <p className="mt-2 text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                You don&apos;t have to wait for the award to harvest high-quality technical answers!
                Click <strong>Harvest to Library</strong> above to review tailored draft sections,
                auto-sanitize client names, and stage verified answers for future RFPs.
              </p>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => {
                    if (onOpenHarvest) onOpenHarvest();
                    else setIsHarvestModalOpen(true);
                  }}
                  className="btn-animate inline-flex items-center gap-1.5 text-xs font-semibold text-[#2ba5b5] hover:underline"
                >
                  <span>Launch Sanitized Harvester</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* STATE B: DECIDED OUTCOME */}
      {isDecided && (
        <div className="space-y-6">
          {/* Outcome Hero Banner */}
          <div
            className={`rounded-2xl border p-6 shadow-xs ${
              outcome === "won"
                ? "border-emerald-300 bg-emerald-50/80 text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-100"
                : outcome === "lost"
                  ? "border-rose-300 bg-rose-50/80 text-rose-950 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-100"
                  : outcome === "disqualified"
                    ? "border-amber-300 bg-amber-50/80 text-amber-950 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100"
                    : "border-slate-300 bg-slate-100/80 text-slate-900 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
            }`}
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-4">
                <div
                  className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ${
                    outcome === "won"
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200"
                      : outcome === "lost"
                        ? "bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-200"
                        : "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200"
                  }`}
                >
                  {outcome === "won" ? (
                    <Trophy className="h-8 w-8 text-amber-500" />
                  ) : outcome === "lost" ? (
                    <XCircle className="h-8 w-8 text-rose-600" />
                  ) : (
                    <AlertTriangle className="h-8 w-8 text-amber-600" />
                  )}
                </div>

                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider opacity-80">
                      Official Outcome
                    </span>
                    <span className="rounded-full bg-white/80 px-2 py-0.5 text-[11px] font-black uppercase tracking-wider dark:bg-slate-900/80">
                      {outcome}
                    </span>
                  </div>
                  <h3 className="mt-1 text-2xl font-black tracking-tight">
                    {outcome === "won"
                      ? "Congratulations! Bid Awarded to MLC"
                      : outcome === "lost"
                        ? "Bid Concluded — Opportunity Awarded to Competitor"
                        : `Bid Concluded (${outcome.toUpperCase()})`}
                  </h3>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs opacity-90">
                    {postMortem?.decidedAt && (
                      <span>
                        Notice Date: <strong>{postMortem.decidedAt}</strong>
                      </span>
                    )}
                    {postMortem?.finalContractValue != null && (
                      <>
                        <span>·</span>
                        <span>
                          Contract Value:{" "}
                          <strong>${postMortem.finalContractValue.toLocaleString("en-US")}</strong>
                        </span>
                      </>
                    )}
                    {postMortem?.winningCompetitor && (
                      <>
                        <span>·</span>
                        <span>
                          Winning Vendor: <strong>{postMortem.winningCompetitor}</strong>
                        </span>
                      </>
                    )}
                    {postMortem?.recordedBy && (
                      <>
                        <span>·</span>
                        <span>
                          Recorded by: <strong>{postMortem.recordedBy}</strong>
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsPostMortemModalOpen(true)}
                  className="btn-animate rounded-xl border border-current/20 bg-white/80 px-3.5 py-2 text-xs font-bold shadow-2xs hover:bg-white dark:bg-slate-900/80 dark:hover:bg-slate-900"
                >
                  Edit Outcome
                </button>
              </div>
            </div>
          </div>

          {/* Core Debrief Information Grid */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Deciding Factors Card */}
            <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-2xs dark:border-slate-800 dark:bg-slate-900">
              <h4 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-[#2ba5b5]" />
                <span>Primary Deciding Drivers</span>
              </h4>
              <p className="mt-1 text-xs text-slate-500">
                Key competitive factors that drove the client&apos;s decision.
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                {postMortem?.factors && postMortem.factors.length > 0 ? (
                  postMortem.factors.map((f) => {
                    const info = FACTOR_LABELS[f] || { label: f, positive: outcome === "won" };
                    return (
                      <span
                        key={f}
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                          outcome === "won"
                            ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                            : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                        }`}
                      >
                        {outcome === "won" ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                        ) : (
                          <AlertOctagon className="h-3.5 w-3.5 text-rose-500" />
                        )}
                        <span>{info.label}</span>
                      </span>
                    );
                  })
                ) : (
                  <span className="text-xs text-slate-400 italic">
                    No specific factors recorded.
                  </span>
                )}
              </div>

              {postMortem?.winningCompetitor && (
                <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950">
                  <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">
                    Competitor Intelligence
                  </div>
                  <div className="mt-1 text-xs font-bold text-slate-800 dark:text-slate-200">
                    Awarded to: {postMortem.winningCompetitor}
                  </div>
                </div>
              )}
            </div>

            {/* Client Debrief & Evaluator Scorecards Card */}
            <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-2xs dark:border-slate-800 dark:bg-slate-900">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4 text-[#2ba5b5]" />
                  <span>Client Debrief & FOIA Scorecards</span>
                </h4>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                    postMortem?.clientDebriefDetails?.status === "received" ||
                    postMortem?.clientDebriefReceived
                      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                      : postMortem?.clientDebriefDetails?.status === "scheduled"
                        ? "bg-teal-100 text-teal-800 dark:bg-teal-950 dark:text-teal-300"
                        : postMortem?.clientDebriefDetails?.status === "requested"
                          ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                          : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                  }`}
                >
                  {postMortem?.clientDebriefDetails?.status?.replace("_", " ") ||
                    (postMortem?.clientDebriefReceived ? "Received" : "Not Requested")}
                </span>
              </div>

              {/* Scorecard numbers */}
              {postMortem?.clientDebriefDetails?.ourScore != null && (
                <div className="mt-4 grid grid-cols-3 gap-2 rounded-xl bg-slate-50 p-3 text-center dark:bg-slate-950">
                  <div>
                    <div className="text-[11px] text-slate-500 font-medium">MLC Score</div>
                    <div className="mt-0.5 text-base font-extrabold text-[#2ba5b5]">
                      {postMortem.clientDebriefDetails.ourScore}
                      {postMortem.clientDebriefDetails.totalPossibleScore && (
                        <span className="text-xs text-slate-400 font-normal">
                          /{postMortem.clientDebriefDetails.totalPossibleScore}
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] text-slate-500 font-medium">Winning Score</div>
                    <div className="mt-0.5 text-base font-extrabold text-emerald-600">
                      {postMortem.clientDebriefDetails.winningScore ?? "N/A"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] text-slate-500 font-medium">Score Delta</div>
                    <div className="mt-0.5 text-base font-extrabold text-slate-700 dark:text-slate-300">
                      {postMortem.clientDebriefDetails.winningScore != null &&
                      postMortem.clientDebriefDetails.ourScore != null
                        ? `${(postMortem.clientDebriefDetails.ourScore - postMortem.clientDebriefDetails.winningScore).toFixed(1)}`
                        : "N/A"}
                    </div>
                  </div>
                </div>
              )}

              {postMortem?.clientFeedbackNotes && (
                <div className="mt-4">
                  <div className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Evaluator Feedback Notes:
                  </div>
                  <blockquote className="mt-1.5 rounded-xl border-l-4 border-[#2ba5b5] bg-slate-50 p-3 text-xs italic text-slate-700 dark:bg-slate-950 dark:text-slate-300">
                    &ldquo;{postMortem.clientFeedbackNotes}&rdquo;
                  </blockquote>
                </div>
              )}

              {!postMortem?.clientFeedbackNotes && !postMortem?.clientDebriefDetails?.ourScore && (
                <div className="mt-4 text-xs text-slate-400 italic">
                  No client debrief notes or evaluator scores recorded yet.
                </div>
              )}
            </div>

            {/* Rebid & Contract Renewal Intelligence Card */}
            <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-2xs dark:border-slate-800 dark:bg-slate-900">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <Clock className="h-4 w-4 text-[#2ba5b5]" />
                  <span>Rebid & Contract Expiry Tracking</span>
                </h4>
                {opportunity.rebidTracking?.isRebidTracked && (
                  <span className="rounded-full bg-teal-100 px-2 py-0.5 text-[10px] font-bold text-teal-800 dark:bg-teal-950 dark:text-teal-300">
                    Tracked
                  </span>
                )}
              </div>

              {opportunity.rebidTracking?.isRebidTracked ? (
                <div className="mt-4 space-y-3">
                  <div className="grid grid-cols-3 gap-2 rounded-xl bg-teal-50/60 p-3 text-center dark:bg-teal-950/20 text-xs">
                    <div>
                      <div className="text-slate-500 font-medium">Start Date</div>
                      <div className="font-bold text-slate-900 dark:text-white mt-0.5">
                        {opportunity.rebidTracking.contractStartDate || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-medium">Max Expiry</div>
                      <div className="font-bold text-slate-900 dark:text-white mt-0.5">
                        {opportunity.rebidTracking.maxExpirationDate || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-500 font-medium">Rebid Alert</div>
                      <div className="font-bold text-[#2ba5b5] mt-0.5">
                        {opportunity.rebidTracking.rebidCaptureNoticeDate || "N/A"}
                      </div>
                    </div>
                  </div>

                  {rebidDays != null && (
                    <div className="rounded-xl border border-teal-200 bg-teal-50/40 p-3 text-xs text-teal-900 dark:border-teal-800 dark:bg-teal-950/30 dark:text-teal-200 flex items-center justify-between">
                      <div>
                        <span>Rebid Capture Countdown: </span>
                        <strong>
                          {rebidDays > 0
                            ? `${rebidDays} days remaining`
                            : `Capture alert is active (${Math.abs(rebidDays)} days ago)`}
                        </strong>
                      </div>
                    </div>
                  )}

                  {opportunity.rebidTracking.rebidOpportunityId ? (
                    <div className="rounded-xl border border-slate-200 p-3 text-xs text-slate-700 dark:border-slate-800 dark:text-slate-300 flex items-center justify-between">
                      <span>Linked Rebid Opportunity:</span>
                      <a
                        href={`/opportunities/${opportunity.rebidTracking.rebidOpportunityId}`}
                        className="btn-animate inline-flex items-center gap-1 font-bold text-[#2ba5b5] hover:underline"
                      >
                        <span>Open Rebid Workspace</span>
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  ) : rebidSuccessId ? (
                    <div className="rounded-xl bg-emerald-50 p-3 text-xs font-semibold text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
                      Rebid opportunity created!{" "}
                      <a href={`/opportunities/${rebidSuccessId}`} className="underline font-bold">
                        Open newly cloned opportunity →
                      </a>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleInitializeRebid}
                      disabled={isCloningRebid}
                      className="btn-animate w-full inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-3.5 py-2.5 text-xs font-bold text-white shadow-2xs hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
                    >
                      {isCloningRebid ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <PlusCircle className="h-4 w-4 text-[#2ba5b5]" />
                      )}
                      <span>Initialize Rebid Opportunity (Stage 1)</span>
                    </button>
                  )}

                  {cloneError && <div className="text-xs text-rose-500">{cloneError}</div>}
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  <p className="text-xs text-slate-500">
                    Track the base term and option years for this contract to trigger automated
                    rebid alerts 6 months before expiration.
                  </p>
                  <button
                    type="button"
                    onClick={() => setIsPostMortemModalOpen(true)}
                    className="btn-animate inline-flex items-center gap-1.5 text-xs font-semibold text-[#2ba5b5] hover:underline"
                  >
                    <span>Configure Rebid Tracking →</span>
                  </button>
                </div>
              )}
            </div>

            {/* Living Library Win Credit & Continuous Learning Card */}
            <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-2xs dark:border-slate-800 dark:bg-slate-900">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#2ba5b5]" />
                  <span>Living Library Win Credit & Learning</span>
                </h4>
                <a
                  href="/library"
                  className="text-xs font-semibold text-[#2ba5b5] hover:underline flex items-center gap-1"
                >
                  <span>Answer Library</span>
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>

              {outcome === "won" ? (
                <div className="mt-4 space-y-3">
                  <div className="rounded-xl bg-emerald-50/60 p-3 text-xs text-emerald-900 dark:bg-emerald-950/20 dark:text-emerald-200">
                    <strong>🏆 Win Credit Stamped: </strong>
                    <span>
                      {loadingAnswers
                        ? "Loading attributed library answers..."
                        : creditedAnswers.length > 0
                          ? `${creditedAnswers.length} library answers attributed with this win!`
                          : `${postMortem?.creditedAnswerIds?.length || 0} library answers credited with this victory.`}
                    </span>
                  </div>

                  {!loadingAnswers && creditedAnswers.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                        Attributed Winning Answers
                      </div>
                      <div className="divide-y divide-slate-100 rounded-xl border border-slate-200/70 dark:divide-slate-800 dark:border-slate-800">
                        {creditedAnswers.map((ans) => (
                          <div
                            key={ans.id}
                            className="flex items-center justify-between p-2.5 text-xs hover:bg-slate-50/50 dark:hover:bg-slate-800/30"
                          >
                            <div className="truncate font-medium text-slate-800 dark:text-slate-200 max-w-[280px]">
                              {ans.title}
                            </div>
                            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                              🏆 {ans.wonBidIds?.length || 1} wins
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  <p className="text-xs text-slate-500">
                    If this proposal lost due to gaps in standard boilerplate or methodology
                    answers, flag them directly for library review so future bids don&apos;t repeat
                    the same issue.
                  </p>

                  {flaggingAnswerId ? (
                    <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50/50 p-3 dark:border-amber-900/40 dark:bg-amber-950/20">
                      <div className="text-xs font-bold text-amber-900 dark:text-amber-200">
                        Flag Answer for Review
                      </div>
                      <input
                        type="text"
                        placeholder="Reason (e.g. Evaluators faulted methodology on Section 2.1)..."
                        value={flagReason}
                        onChange={(e) => setFlagReason(e.target.value)}
                        className="w-full rounded-lg border border-slate-200 bg-white p-2 text-xs text-slate-900 outline-hidden dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setFlaggingAnswerId(null)}
                          className="rounded-lg px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => handleFlagAnswer(flaggingAnswerId)}
                          disabled={isSubmittingFlag || !flagReason.trim()}
                          className="rounded-lg bg-amber-600 px-3 py-1 text-xs font-bold text-white hover:bg-amber-700"
                        >
                          {isSubmittingFlag ? "Submitting..." : "Submit Flag"}
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}

              <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => {
                    if (onOpenHarvest) onOpenHarvest();
                    else setIsHarvestModalOpen(true);
                  }}
                  className="btn-animate inline-flex items-center gap-1.5 rounded-xl bg-slate-100 px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                >
                  <Sparkles className="h-3.5 w-3.5 text-[#2ba5b5]" />
                  <span>Harvest Custom Written Sections to Library</span>
                </button>
              </div>
            </div>
          </div>

          {/* Internal Lessons Learned Retrospective Banner */}
          {postMortem?.lessonsLearned && (
            <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-2xs dark:border-slate-800 dark:bg-slate-900">
              <h4 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <HelpCircle className="h-4 w-4 text-[#2ba5b5]" />
                <span>Internal Lessons Learned & Retrospective Takeaways</span>
              </h4>
              <p className="mt-2 text-xs leading-relaxed text-slate-700 dark:text-slate-300 whitespace-pre-line">
                {postMortem.lessonsLearned}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Post Mortem Debrief Modal */}
      {isPostMortemModalOpen && (
        <PostMortemModal
          isOpen={isPostMortemModalOpen}
          opportunity={opportunity}
          user={user}
          onClose={() => setIsPostMortemModalOpen(false)}
          onOpenHarvest={() => {
            setIsPostMortemModalOpen(false);
            if (onOpenHarvest) onOpenHarvest();
            else setIsHarvestModalOpen(true);
          }}
        />
      )}

      {/* Harvest to Living Library Modal */}
      {isHarvestModalOpen && (
        <HarvestLibraryModal
          isOpen={isHarvestModalOpen}
          opportunity={opportunity}
          user={user}
          onClose={() => setIsHarvestModalOpen(false)}
        />
      )}
    </div>
  );
};
